import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search as SearchIcon, BrainCircuit, Filter, FileText, ChevronRight, TextSelect, Layers, AlertTriangle, ExternalLink, MessageSquarePlus, CheckCircle2, AlertOctagon, Info, BookOpen, Copy, ThumbsUp, ThumbsDown } from 'lucide-react';
interface SearchProps {
  initialQuery?: string;
}
export function Search({
  initialQuery
}: SearchProps) {
  const [query, setQuery] = useState(initialQuery || '');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic' | 'hybrid'>('semantic');
  const [showFilters, setShowFilters] = useState(false);
  const [showNoAnswerState, setShowNoAnswerState] = useState(false);
  const SUGGESTED_QUERIES = ['What treatment did Ramirez receive after the October 2023 collision?', 'Find prior demands involving lumbar disc injury.', 'What records mention Dr. Smith?', 'Which documents discuss lost wages?', 'Summarize the driver fatigue evidence in Ramirez.'];

  // Mock data for successful answer
  const MOCK_ANSWER = {
    text: 'Following the October 12, 2023 collision, plaintiff Ramirez received immediate emergency care followed by a course of conservative orthopedic treatment and physical therapy.\n\nSpecifically, Ramirez was transported via EMS to Mercy Hospital ER on the day of the incident, where he was treated for severe L4-L5 pain and administered Morphine [1]. Two days later, on October 14, 2023, he had an initial consultation with Dr. Smith (Orthopedics), who ordered an MRI of the lumbar spine [2]. The MRI, performed on October 18, revealed an acute disc herniation at L4-L5 with nerve root impingement [3]. Subsequently, Ramirez began a 12-week course of physical therapy at PT Rehab Center, attending 24 sessions between November 2023 and January 2024 [4].',
    confidence: 94,
    answerType: 'sourced',
    citations: [{
      id: 1,
      docTitle: 'Mercy Hospital ER Records',
      bates: 'RLSF-0014',
      page: 'p. 2',
      relevance: 98,
      snippet: 'Patient admitted via EMS. Complains of severe L4-L5 pain post-collision. Administered 5mg Morphine IV. Discharged with pain management instructions.'
    }, {
      id: 2,
      docTitle: 'Dr. Smith Orthopedic Consultation Notes',
      bates: 'RLSF-0056',
      page: 'p. 1',
      relevance: 95,
      snippet: 'Initial consult. MRI ordered for lumbar spine to evaluate radicular symptoms following MVA. Patient reports numbness in left foot.'
    }, {
      id: 3,
      docTitle: 'Mercy Imaging MRI Report',
      bates: 'RLSF-0074',
      page: 'p. 1',
      relevance: 99,
      snippet: 'Findings: Acute disc herniation at L4-L5 with left-sided nerve root impingement. No prior imaging available for comparison.'
    }, {
      id: 4,
      docTitle: 'PT Rehab Center Progress Notes',
      bates: 'RLSF-0095',
      page: 'p. 12',
      relevance: 88,
      snippet: 'Patient completed 24/24 prescribed PT sessions for lumbar stabilization. Reports pain level decreased from 8/10 to 4/10.'
    }],
    inferredStatements: [{
      statement: 'The 12-week duration is calculated from the first PT visit (Nov 2, 2023) to the final progress note (Jan 24, 2024).',
      confidence: 85
    }],
    relatedQuestions: ['Did Ramirez report any prior back pain before the collision?', 'What were the total medical expenses for the Mercy Hospital visit?', 'Has Dr. Smith recommended surgical intervention?']
  };
  // Mock data for insufficient evidence state
  const MOCK_NO_ANSWER = {
    query: 'What was the exact speed of the truck at impact?',
    reason: 'The corpus does not contain sufficient evidence to answer this question with confidence.',
    partialFindings: [{
      finding: 'The police report mentions "high rate of speed" but does not specify an exact figure.',
      source: 'Police Accident Report',
      bates: 'RLSF-0115',
      confidence: 45
    }, {
      finding: 'Dr. Allen\'s accident reconstruction report references "estimated impact velocity" but the specific page was not successfully OCR\'d (quality: 42%).',
      source: 'Expert Report — Dr. Allen',
      bates: 'RLSF-0312',
      confidence: 32
    }],
    suggestedActions: ['Request re-scan of Dr. Allen expert report pages 8-12', "Subpoena truck's EDR (Event Data Recorder) data", 'Review deposition transcripts for speed testimony']
  };
  const MOCK_DOCUMENTS = [{
    id: 'doc1',
    title: 'Mercy Hospital ER Records',
    type: 'Medical Record',
    date: 'Oct 12, 2023',
    relevance: 98,
    bates: 'RLSF-0013 — 0054',
    excerpt: "...Patient admitted via EMS. Complains of <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>severe L4-L5 pain</mark> post-collision. Administered 5mg Morphine IV..."
  }, {
    id: 'doc2',
    title: 'Mercy Imaging MRI Report',
    type: 'Medical Record',
    date: 'Oct 18, 2023',
    relevance: 95,
    bates: 'RLSF-0073 — 0078',
    excerpt: "...Findings: <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>Acute disc herniation at L4-L5</mark> with left-sided nerve root impingement..."
  }, {
    id: 'doc3',
    title: 'Dr. Smith Orthopedic Consultation Notes',
    type: 'Medical Record',
    date: 'Oct 14, 2023',
    relevance: 92,
    bates: 'RLSF-0055 — 0072',
    excerpt: "...Initial consult. <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>MRI ordered for lumbar spine</mark> to evaluate radicular symptoms following MVA..."
  }, {
    id: 'doc4',
    title: 'PT Rehab Center Progress Notes',
    type: 'Medical Record',
    date: 'Jan 24, 2024',
    relevance: 85,
    bates: 'RLSF-0079 — 0110',
    excerpt: "...Patient completed 24/24 prescribed PT sessions for <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>lumbar stabilization</mark>..."
  }, {
    id: 'doc5',
    title: "Plaintiff's Responses to Interrogatories",
    type: 'Discovery',
    date: 'Dec 15, 2023',
    relevance: 78,
    bates: 'RLSF-DISC-0012',
    excerpt: "...INTERROGATORY NO. 8: Describe all <mark class='bg-yellow-100 text-yellow-900 rounded px-0.5'>medical treatment received</mark> as a result of the incident..."
  }];
  const handleSearch = (e?: React.FormEvent, searchQuery?: string) => {
    if (e) e.preventDefault();
    const q = searchQuery || query;
    if (!q.trim()) return;
    setQuery(q);
    setIsSearching(true);
    setHasSearched(false);
    setShowNoAnswerState(q.toLowerCase().includes('speed'));
    setTimeout(() => {
      setIsSearching(false);
      setHasSearched(true);
    }, 1500);
  };
  // Auto-run search when an initial query is passed in (e.g., from the global header search)
  useEffect(() => {
    const q = initialQuery?.trim();
    if (!q) return;
    setQuery(q);
    setIsSearching(true);
    setHasSearched(false);
    setShowNoAnswerState(q.toLowerCase().includes('speed'));
    const timer = setTimeout(() => {
      setIsSearching(false);
      setHasSearched(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [initialQuery]);
  return <div className="space-y-6 pb-12" data-id="element-2108">
      {/* Page Header */}
      <div className="flex flex-col space-y-2" data-id="element-2109">
        <div className="flex items-center space-x-3" data-id="element-2110">
          <div className="p-2 bg-blue-100 text-blue-700 rounded-lg" data-id="element-2111">
            <BrainCircuit className="w-6 h-6" data-id="element-2112" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight" data-id="element-2113">
            Search & Grounded Answers
          </h1>
        </div>
        <p className="text-muted-foreground" data-id="element-2114">
          Query the local corpus. All answers are grounded in indexed documents
          with verifiable citations.
        </p>
      </div>

      {/* Search Bar Area */}
      <div className="space-y-4" data-id="element-2115">
        <form onSubmit={handleSearch} className="relative" data-id="element-2116">
          <div className="relative flex items-center" data-id="element-2117">
            <SearchIcon className="absolute left-4 w-5 h-5 text-muted-foreground" data-id="element-2118" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} className="w-full h-14 pl-12 pr-32 rounded-xl border-2 border-input bg-background text-lg shadow-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" placeholder="Ask about a matter, document set, provider, or issue..." data-id="element-2119" />

            <div className="absolute right-2 flex space-x-2" data-id="element-2120">
              <Button type="button" variant={showFilters ? 'secondary' : 'ghost'} size="sm" className="h-10 text-muted-foreground" onClick={() => setShowFilters(!showFilters)} data-id="element-2121">

                <Filter className="w-4 h-4" data-id="element-2122" />
              </Button>
              <Button type="submit" className="h-10 px-6" disabled={isSearching || !query.trim()} data-id="element-2123">

                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>
        </form>

        {/* Search Mode Toggle & Suggested Queries */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2124">
          <div className="inline-flex bg-muted p-1 rounded-lg" data-id="element-2125">
            <button type="button" onClick={() => setSearchMode('keyword')} className={`flex items-center space-x-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${searchMode === 'keyword' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-2126">

              <TextSelect className="w-4 h-4" data-id="element-2127" />
              <span data-id="element-2128">Keyword</span>
            </button>
            <button type="button" onClick={() => setSearchMode('semantic')} className={`flex items-center space-x-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${searchMode === 'semantic' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-2129">

              <BrainCircuit className="w-4 h-4" data-id="element-2130" />
              <span data-id="element-2131">Semantic</span>
            </button>
            <button type="button" onClick={() => setSearchMode('hybrid')} className={`flex items-center space-x-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${searchMode === 'hybrid' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-2132">

              <Layers className="w-4 h-4" data-id="element-2133" />
              <span data-id="element-2134">Hybrid</span>
            </button>
          </div>

          {!hasSearched && !isSearching && <div className="flex flex-wrap gap-2" data-id="element-2135">
              {SUGGESTED_QUERIES.slice(0, 3).map((q, i) => <button key={i} onClick={() => handleSearch(undefined, q)} className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full transition-colors border border-blue-200" data-id="element-2136">

                  {q}
                </button>)}
            </div>}
        </div>

        {/* Collapsible Filter Bar */}
        {showFilters && <Card className="bg-muted/30 border-dashed animate-in slide-in-from-top-2 duration-200" data-id="element-2137">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2138">
              <div className="space-y-1.5" data-id="element-2139">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2140">
                  Matter
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2141">
                  <option data-id="element-2142">All Matters</option>
                  <option data-id="element-2143">Ramirez v. Lone Star Freight</option>
                  <option data-id="element-2144">Castillo v. Westfield Properties</option>
                  <option data-id="element-2145">Ortega v. Gulf Coast Industrial</option>
                  <option data-id="element-2146">Garcia v. Metro Transit Authority</option>
                  <option data-id="element-2147">Nguyen v. St. Luke's Medical Center</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2148">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2149">
                  Document Type
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2150">
                  <option data-id="element-2151">All Types</option>
                  <option data-id="element-2152">Medical Records</option>
                  <option data-id="element-2153">Pleadings</option>
                  <option data-id="element-2154">Discovery Responses</option>
                  <option data-id="element-2155">Depositions</option>
                  <option data-id="element-2156">Expert Reports</option>
                  <option data-id="element-2157">Correspondence</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2158">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2159">
                  Date Range
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2160">
                  <option data-id="element-2161">Any Time</option>
                  <option data-id="element-2162">Last 7 Days</option>
                  <option data-id="element-2163">Last 30 Days</option>
                  <option data-id="element-2164">Last 90 Days</option>
                  <option data-id="element-2165">Last Year</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2166">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2167">
                  Source
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2168">
                  <option data-id="element-2169">All Sources</option>
                  <option data-id="element-2170">Local Upload</option>
                  <option data-id="element-2171">Email Import</option>
                  <option data-id="element-2172">Dropbox</option>
                  <option data-id="element-2173">iCloud</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2174">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2175">
                  OCR Quality
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2176">
                  <option data-id="element-2177">Any Quality</option>
                  <option data-id="element-2178">High (&gt;90%)</option>
                  <option data-id="element-2179">Medium (70-90%)</option>
                  <option data-id="element-2180">Low (&lt;70%)</option>
                </select>
              </div>
              <div className="space-y-1.5" data-id="element-2181">
                <label className="text-xs font-medium text-muted-foreground" data-id="element-2182">
                  Review State
                </label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-id="element-2183">
                  <option data-id="element-2184">Any State</option>
                  <option data-id="element-2185">Pending Review</option>
                  <option data-id="element-2186">Approved</option>
                  <option data-id="element-2187">Flagged</option>
                  <option data-id="element-2188">Privileged</option>
                </select>
              </div>
            </CardContent>
          </Card>}
      </div>

      {/* Loading State */}
      {isSearching && <div className="py-24 flex flex-col items-center justify-center space-y-6 text-muted-foreground" data-id="element-2189">
          <div className="relative" data-id="element-2190">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-2191"></div>
            <BrainCircuit className="w-5 h-5 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-2192" />
          </div>
          <div className="text-center space-y-2" data-id="element-2193">
            <p className="text-lg font-medium text-foreground" data-id="element-2194">
              Synthesizing Answer...
            </p>
            <p className="text-sm" data-id="element-2195">
              Scanning 14,205 documents in the local corpus.
            </p>
          </div>
        </div>}

      {/* NO ANSWER FOUND STATE */}
      {hasSearched && !isSearching && showNoAnswerState && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2196">
          <Card className="border-red-200 bg-red-50/30 shadow-md" data-id="element-2197">
            <CardHeader className="pb-3 border-b border-red-100" data-id="element-2198">
              <div className="flex items-start space-x-3" data-id="element-2199">
                <div className="p-2 bg-red-100 rounded-lg" data-id="element-2200">
                  <AlertOctagon className="w-6 h-6 text-red-600" data-id="element-2201" />
                </div>
                <div className="flex-1" data-id="element-2202">
                  <CardTitle className="text-lg text-red-900" data-id="element-2203">
                    Insufficient Evidence to Answer
                  </CardTitle>
                  <CardDescription className="text-red-700 mt-1" data-id="element-2204">
                    The system could not find sufficient supporting
                    documentation to provide a reliable answer to this query.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6" data-id="element-2205">
              <div className="bg-white rounded-lg border border-red-100 p-4" data-id="element-2206">
                <p className="text-sm font-medium text-foreground mb-2" data-id="element-2207">
                  Query:
                </p>
                <p className="text-foreground italic" data-id="element-2208">
                  "{MOCK_NO_ANSWER.query}"
                </p>
              </div>

              <div data-id="element-2209">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center" data-id="element-2210">
                  <Info className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2211" />
                  Partial Findings (Low Confidence)
                </h4>
                <div className="space-y-3" data-id="element-2212">
                  {MOCK_NO_ANSWER.partialFindings.map((finding, i) => <div key={i} className="bg-amber-50/50 border border-amber-200 rounded-lg p-4" data-id="element-2213">

                      <div className="flex items-start justify-between mb-2" data-id="element-2214">
                        <div className="flex items-center space-x-2" data-id="element-2215">
                          <Badge variant="outline" className="text-[10px] bg-white font-mono" data-id="element-2216">

                            {finding.bates}
                          </Badge>
                          <span className="text-xs text-muted-foreground" data-id="element-2217">
                            {finding.source}
                          </span>
                        </div>
                        <Badge variant="destructive" className="text-[10px]" data-id="element-2218">
                          {finding.confidence}% Conf.
                        </Badge>
                      </div>
                      <p className="text-sm text-foreground" data-id="element-2219">
                        {finding.finding}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2220">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center" data-id="element-2221">
                  <BookOpen className="w-4 h-4 mr-2" data-id="element-2222" />
                  Suggested Actions to Resolve
                </h4>
                <ul className="space-y-2" data-id="element-2223">
                  {MOCK_NO_ANSWER.suggestedActions.map((action, i) => <li key={i} className="flex items-start text-sm text-blue-800" data-id="element-2224">

                      <ChevronRight className="w-4 h-4 mr-2 text-blue-500 shrink-0 mt-0.5" data-id="element-2225" />
                      {action}
                    </li>)}
                </ul>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-center text-muted-foreground flex items-center justify-center" data-id="element-2226">
            <AlertTriangle className="w-3 h-3 mr-1.5" data-id="element-2227" />
            This query returned insufficient evidence. Consider refining your
            search or reviewing the suggested actions above.
          </p>
        </div>}

      {/* SUCCESSFUL ANSWER STATE */}
      {hasSearched && !isSearching && !showNoAnswerState && <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2228">
          {/* Left Pane: Ranked Documents */}
          <div className="lg:col-span-5 space-y-4" data-id="element-2229">
            <div className="flex items-center justify-between pb-2 border-b" data-id="element-2230">
              <h3 className="font-semibold text-lg flex items-center" data-id="element-2231">
                <FileText className="w-5 h-5 mr-2 text-muted-foreground" data-id="element-2232" />
                Source Documents
              </h3>
              <Badge variant="secondary" data-id="element-2233">{MOCK_DOCUMENTS.length} Results</Badge>
            </div>

            <div className="space-y-3" data-id="element-2234">
              {MOCK_DOCUMENTS.map((doc, index) => <Card key={doc.id} className="hover:border-blue-300 transition-colors cursor-pointer group shadow-sm" data-id="element-2235">

                  <CardContent className="p-4" data-id="element-2236">
                    <div className="flex items-start justify-between mb-2" data-id="element-2237">
                      <div className="flex items-start space-x-3" data-id="element-2238">
                        <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors" data-id="element-2239">
                          {index + 1}
                        </div>
                        <div data-id="element-2240">
                          <h4 className="font-medium text-sm group-hover:text-blue-600 transition-colors line-clamp-1" title={doc.title} data-id="element-2241">

                            {doc.title}
                          </h4>
                          <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-1" data-id="element-2242">
                            <span data-id="element-2243">{doc.type}</span>
                            <span data-id="element-2244">•</span>
                            <span data-id="element-2245">{doc.date}</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant={doc.relevance > 90 ? 'success' : 'secondary'} className="text-[10px] shrink-0 ml-2" data-id="element-2246">

                        {doc.relevance}%
                      </Badge>
                    </div>

                    <div className="mt-3 text-xs text-muted-foreground bg-muted/30 p-2.5 rounded border border-border/50" data-id="element-2247">
                      <div className="font-mono text-[10px] text-blue-600 font-medium mb-1" data-id="element-2248">
                        {doc.bates}
                      </div>
                      <p className="italic leading-relaxed" dangerouslySetInnerHTML={{
                  __html: doc.excerpt
                }} data-id="element-2249">
                  </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {/* Right Pane: Grounded Answer */}
          <div className="lg:col-span-7 space-y-6" data-id="element-2250">
            {/* Main Answer Card */}
            <Card className="border-blue-200 shadow-lg relative overflow-hidden" data-id="element-2251">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-500" data-id="element-2252"></div>
              <CardHeader className="pb-3 bg-blue-50/50 border-b border-blue-100" data-id="element-2253">
                <div className="flex items-start justify-between" data-id="element-2254">
                  <div className="flex items-center space-x-2" data-id="element-2255">
                    <BrainCircuit className="w-5 h-5 text-blue-600" data-id="element-2256" />
                    <CardTitle className="text-lg" data-id="element-2257">Grounded Answer</CardTitle>
                  </div>
                  <div className="flex items-center space-x-3" data-id="element-2258">
                    <div className="flex items-center space-x-1.5" data-id="element-2259">
                      <span className="text-xs text-muted-foreground" data-id="element-2260">
                        Confidence:
                      </span>
                      <Badge variant="success" className="flex items-center space-x-1" data-id="element-2261">

                        <CheckCircle2 className="w-3 h-3" data-id="element-2262" />
                        <span data-id="element-2263">{MOCK_ANSWER.confidence}%</span>
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6" data-id="element-2264">
                {/* Answer Type Indicator */}
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start space-x-3" data-id="element-2265">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" data-id="element-2266" />
                  <div data-id="element-2267">
                    <p className="text-sm font-medium text-emerald-900" data-id="element-2268">
                      Fully Sourced Answer
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5" data-id="element-2269">
                      All factual claims in this answer are directly supported
                      by citations to indexed documents.
                    </p>
                  </div>
                </div>

                {/* Answer Text with Citations */}
                <div className="prose prose-sm max-w-none text-foreground leading-relaxed whitespace-pre-wrap" data-id="element-2270">
                {MOCK_ANSWER.text.split(/(\[\d+\])/).map((part, i) => {
                if (part.match(/\[\d+\]/)) {
                  const citationNum = part.replace(/[[\]]/g, '');
                  const citationData = MOCK_ANSWER.citations.find(c => c.id === parseInt(citationNum));
                  return <sup key={i} className="mx-0.5" data-id="element-2271">
                          <a href={`#citation-${citationNum}`} title={citationData ? `[${citationNum}] ${citationData.docTitle} — ${citationData.bates}` : `Citation ${citationNum}`} className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-600 text-white hover:bg-blue-700 hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 text-[10px] font-bold transition-all no-underline cursor-pointer" data-id="element-2272">

                            {citationNum}
                          </a>
                        </sup>;
                }
                return <span key={i} data-id="element-2273">{part}</span>;
              })}
                </div>

                {/* Inferred Statements Warning */}
                {MOCK_ANSWER.inferredStatements.length > 0 && <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-2274">
                    <div className="flex items-start space-x-2" data-id="element-2275">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" data-id="element-2276" />
                      <div data-id="element-2277">
                        <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-1" data-id="element-2278">
                          Inferred Statement
                        </p>
                        {MOCK_ANSWER.inferredStatements.map((stmt, i) => <div key={i} className="flex items-center flex-wrap gap-2 text-sm text-amber-800" data-id="element-2279">
                            <span data-id="element-2280">{stmt.statement}</span>
                            <Badge variant="outline" className="ml-1 text-[10px] bg-white shrink-0" data-id="element-2281">

                              {stmt.confidence}% conf.
                            </Badge>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </CardContent>
              <CardFooter className="p-4 border-t bg-muted/20 flex items-center justify-between" data-id="element-2282">
                <div className="flex items-center space-x-2 text-xs text-muted-foreground" data-id="element-2283">
                  <span data-id="element-2284">Was this answer helpful?</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" data-id="element-2285">
                    <ThumbsUp className="w-3 h-3" data-id="element-2286" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2" data-id="element-2287">
                    <ThumbsDown className="w-3 h-3" data-id="element-2288" />
                  </Button>
                </div>
                <div className="flex space-x-2" data-id="element-2289">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" data-id="element-2290">

                    <Copy className="w-3 h-3 mr-1" data-id="element-2291" /> Copy
                  </Button>
                </div>
              </CardFooter>
            </Card>

            {/* Supporting Citations Panel */}
            <Card className="shadow-sm" data-id="element-2292">
              <CardHeader className="py-3 border-b bg-muted/20" data-id="element-2293">
                <CardTitle className="text-sm flex items-center" data-id="element-2294">
                  <BookOpen className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2295" />
                  Citation Details ({MOCK_ANSWER.citations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0" data-id="element-2296">
                <div className="divide-y" data-id="element-2297">
                  {MOCK_ANSWER.citations.map(citation => <div key={citation.id} id={`citation-${citation.id}`} className="p-4 hover:bg-muted/30 transition-colors group" data-id="element-2298">

                      <div className="flex items-start space-x-3" data-id="element-2299">
                        <div className="w-6 h-6 rounded bg-blue-600 text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" data-id="element-2300">
                          {citation.id}
                        </div>
                        <div className="flex-1 space-y-2" data-id="element-2301">
                          <div className="flex items-start justify-between" data-id="element-2302">
                            <div data-id="element-2303">
                              <h5 className="text-sm font-medium text-foreground group-hover:text-blue-600 transition-colors flex items-center" data-id="element-2304">
                                {citation.docTitle}
                                <ExternalLink className="w-3 h-3 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2305" />
                              </h5>
                              <div className="flex items-center space-x-2 mt-0.5" data-id="element-2306">
                                <span className="text-xs text-blue-600 font-mono font-medium" data-id="element-2307">
                                  {citation.bates}
                                </span>
                                <span className="text-xs text-muted-foreground" data-id="element-2308">
                                  {citation.page}
                                </span>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-[10px] bg-white" data-id="element-2309">

                              {citation.relevance}% match
                            </Badge>
                          </div>
                          <div className="text-sm text-foreground/80 bg-muted/50 p-3 rounded border-l-2 border-blue-300 italic" data-id="element-2310">
                            "{citation.snippet}"
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </CardContent>
              <CardFooter className="p-3 border-t bg-muted/20" data-id="element-2311">
                <Button variant="outline" size="sm" className="w-full text-xs" data-id="element-2312">
                  <ExternalLink className="w-3 h-3 mr-2" data-id="element-2313" />
                  Open All Cited Documents in Workspace
                </Button>
              </CardFooter>
            </Card>

            {/* Related Questions */}
            <Card className="shadow-sm border-dashed" data-id="element-2314">
              <CardHeader className="py-3 border-b" data-id="element-2315">
                <CardTitle className="text-sm flex items-center text-muted-foreground" data-id="element-2316">
                  <MessageSquarePlus className="w-4 h-4 mr-2" data-id="element-2317" />
                  Follow-up Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3" data-id="element-2318">
                <div className="flex flex-col space-y-2" data-id="element-2319">
                  {MOCK_ANSWER.relatedQuestions.map((q, i) => <button key={i} onClick={() => handleSearch(undefined, q)} className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline py-1.5 px-2 rounded hover:bg-blue-50 transition-colors flex items-center" data-id="element-2320">

                      <ChevronRight className="w-3 h-3 mr-1.5 text-blue-400" data-id="element-2321" />
                      {q}
                    </button>)}
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <p className="text-xs text-center text-muted-foreground flex items-center justify-center" data-id="element-2322">
              <AlertTriangle className="w-3 h-3 mr-1.5" data-id="element-2323" />
              AI-assisted results require attorney review. Confidence scores
              reflect document relevance, not legal accuracy.
            </p>
          </div>
        </div>}
    </div>;
}